import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Lock, Loader2, Eye, EyeOff, ShieldCheck, Users, ClipboardCheck, CalendarDays, ArrowLeft, Sparkles } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { authAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'

export default function LoginPage() {
  const navigate = useNavigate()
  const { success: toastSuccess, error: toastError } = useToast()
  const { setUser, setTokens } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      toastError('Please enter your email address.')
      return
    }
    if (!password) {
      toastError('Please enter your password.')
      return
    }

    setIsLoading(true)

    try {
      const response = await authAPI.login(email, password)
      const { access, refresh, role, employee_id, full_name } = response.data

      setTokens(access, refresh)

      const user = {
        id: employee_id,
        email: response.data.email || email,
        full_name: full_name,
        role: role.toLowerCase(),
        employee_id: employee_id,
        mobile: response.data.mobile || null,
        photo: response.data.photo || null,
      }
      setUser(user)

      toastSuccess(`Welcome back, ${full_name || 'there'}!`)
      const portal = role.toLowerCase() === 'viewer' ? 'admin' : role.toLowerCase()
      navigate(`/${portal}/dashboard`)
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Login failed. Please check your credentials and try again.')
      setError(errorMsg)
      toastError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full flex bg-[#FAF6EE]">
      {/* Left panel — modern branding */}
      <div className="relative hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between overflow-hidden bg-[#0F172A] text-white p-12 xl:p-16">
        {/* Animated gradient orbs */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#F0821F]/30 blur-3xl animate-pulse" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 rounded-full bg-[#A31621]/30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-[#E4670F]/20 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Top bar */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-black/20">
              <img src="/logo.png" alt="IRCS Haryana" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <p className="font-bold leading-tight text-lg">HRMS</p>
              <p className="text-xs text-white/60 leading-tight">Human Resource Management</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1 text-[11px] font-semibold tracking-wider uppercase">
            <Sparkles className="w-3 h-3" />
            Since 1966
          </span>
        </div>

        {/* Center content */}
        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F0821F]/20 to-[#E4670F]/20 border border-[#F0821F]/30 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide text-[#FBBF6E] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F0821F] animate-pulse" />
            INDIAN RED CROSS SOCIETY · HARYANA
          </div>

          <h1
            className="text-5xl xl:text-6xl font-bold leading-[1.05] mb-6 tracking-tight"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Employee
            <br />
            <span className="bg-gradient-to-r from-[#F0821F] via-[#E4670F] to-[#A31621] bg-clip-text text-transparent">
              Management Portal
            </span>
          </h1>

          <p className="text-white/70 text-base leading-relaxed mb-10 max-w-md">
            A dedicated workspace to manage attendance, submit reports, and coordinate leave — all in one place.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: ClipboardCheck, label: 'Attendance', accent: 'from-[#F0821F] to-[#E4670F]' },
              { icon: Users, label: 'Records', accent: 'from-[#E4670F] to-[#A31621]' },
              { icon: CalendarDays, label: 'Leaves', accent: 'from-[#A31621] to-[#7A1F1F]' },
            ].map(({ icon: Icon, label, accent }) => (
              <div
                key={label}
                className="group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-4 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 cursor-default"
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-3 shadow-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-white/90">{label}</p>
                <p className="text-[11px] text-white/50 mt-0.5">Manage</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-3 text-white/40 text-xs">
          <div className="flex-1 h-px bg-white/10" />
          <span>© {new Date().getFullYear()} IRCS Haryana</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 relative">
        {/* Mobile top brand */}
        <div className="lg:hidden w-full max-w-md mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-md ring-1 ring-neutral-200">
              <img src="/logo.png" alt="IRCS Haryana" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#7A1F1F]" style={{ fontFamily: 'Georgia, serif' }}>
                HRMS
              </h1>
              <p className="text-neutral-500 text-xs">Human Resource Management System</p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md">
          {/* Desktop back button */}
          <button
            onClick={() => navigate('/')}
            className="hidden lg:inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 mb-8 transition-colors text-sm font-medium px-3 py-1.5 -ml-3 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> Back to portal selection
          </button>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-neutral-200/60 ring-1 ring-neutral-200/60 p-8 sm:p-10">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Welcome back
              </h2>
              <p className="text-sm text-neutral-500">
                Sign in with your registered employee credentials.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@ircs.in"
                    className="w-full pl-11 pr-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-neutral-700">
                    Password
                  </label>
                  <button
                    type="button"
                    className="text-xs font-medium text-[#E4670F] hover:text-[#A31621] transition-colors"
                  >
                    Forgot?
                  </button>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="•••••••••"
                    className="w-full pl-11 pr-12 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full relative bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] disabled:from-neutral-400 disabled:to-neutral-400 text-white font-semibold py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-[#A31621]/20 hover:shadow-xl hover:shadow-[#A31621]/30 hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:shadow-none"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="flex items-center justify-center gap-2 mt-8 pt-6 border-t border-neutral-100 text-neutral-400 text-xs">
              <ShieldCheck className="w-4 h-4" />
              <span>Authorized personnel only · Secured by HRMS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}